import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import axios from 'axios';
import { useInView } from 'react-intersection-observer';
import {
  ILearningCenterArticle,
  ILearningCenterArticleSectionBlockFields
} from '../../../../../types/contentful';
import { BlockContainer, Column } from '../../../Layout';
import { Flexbox } from '../../../Flexbox';
import { ArticleSnippetList } from '../ArticleSnippetList';
import { HeaderText } from './styles';
import { isCanadianContext } from '../../../../lib/geolocation/linkUtils';

interface ArticlesResponse {
  items: ILearningCenterArticle[];
  isLastPage?: boolean;
}

export function ArticleSectionBlock({
  title,
  category,
  lazyLoad
}: ILearningCenterArticleSectionBlockFields) {
  const router = useRouter()?.query;
  const partnerResourcesCategory =
    useRouter().asPath.includes('partner-resources');
  const [articlesList, setArticlesList] = useState<ILearningCenterArticle[]>(
    []
  );
  const [articlesBuffer, setArticlesBuffer] = useState<
    ILearningCenterArticle[]
  >([]);
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(0);
  const [lastPage, setLastPage] = useState(true);
  const [loadNextPage, setLoadNextPage] = useState(false);
  const [finishedLoading, setFinishedLoading] = useState(false);
  const { ref, inView } = useInView();

  const savedFilters = {
    difficulties: router?.difficulties?.toString() ?? '',
    mediaTypes: router?.mediaTypes?.toString() ?? '',
    sortBy: router?.sortBy?.toString() ?? '',
    subject: router?.subject?.toString() ?? '',
    topics: router?.topics?.toString() ?? ''
  };

  useEffect(() => {
    if ((lazyLoad && !inView) || !loadNextPage || finishedLoading) {
      return;
    }
    if (lastPage && articlesBuffer.length > 0) {
      setArticlesList(articlesBuffer);
      setArticlesBuffer([]);
      setFinishedLoading(true);
      return;
    }
    fetchArticles(category ? category.fields.name : null, pageNumber, pageSize)
      .then(({ items, isLastPage }) => {
        const allItems = [...articlesBuffer, ...items];
        const itemsToAdd = allItems.slice(0, pageSize);
        const itemsToBuffer = allItems.slice(pageSize);

        setArticlesList(itemsToAdd);
        setArticlesBuffer(itemsToBuffer);

        if (isLastPage !== undefined) {
          setLastPage(isLastPage);
        }
      })
      .finally(() => setLoadNextPage(false));
  }, [
    lazyLoad,
    inView,
    loadNextPage,
    pageNumber,
    pageSize,
    savedFilters,
    articlesBuffer,
    finishedLoading
  ]);

  const fetchArticles = async (
    categoryName: string | null,
    pageNum: number,
    pageLimit: number
  ): Promise<ArticlesResponse> => {
    try {
      const isCanada = isCanadianContext();
      const countryParam = isCanada ? '&country=CA' : '&country=US';

      //Logic to decide whether to use category api or filters api
      if (
        router?.category ||
        categoryName == 'All Categories' ||
        partnerResourcesCategory
      ) {
        const url = `/api/learning-center/articles-by-category?category=${categoryName}&page=${pageNum}&limit=${pageLimit}${countryParam}`;
        const { data } = await axios.get<ArticlesResponse>(url);
        return data;
      } else {
        const url = `/api/learning-center/filtered-articles?difficulties=${savedFilters.difficulties}&mediaTypes=${savedFilters.mediaTypes}&sortBy=${savedFilters.sortBy}&subject=${savedFilters.subject}&topics=${savedFilters.topics}&page=${pageNum}&limit=${pageLimit}${countryParam}`;
        const { data } = await axios.get<ArticlesResponse>(url);
        return data;
      }
    } catch (error) {
      console.error('Error loading articles', error);
      return { items: [] } as ArticlesResponse;
    }
  };

  return (
    <div className="mb-16 desktop:mb-20">
      <BlockContainer data-testid="article-section-block">
        <Flexbox flexDirection="column" className="w-full" ref={ref}>
          <Column size={12} padding={{ 0: 'm', desktop: 'ml' }}>
            <HeaderText>{title == 'All Categories' ? '' : title}</HeaderText>
          </Column>
          <Column size={12} padding={{ mobile: 0 }}>
            <ArticleSnippetList
              articles={articlesList}
              lastPage={lastPage && articlesBuffer.length === 0}
              onLoadMore={(newPageNumber: number, newPageSize: number) => {
                setPageNumber(newPageNumber);
                setPageSize(newPageSize);
                setLoadNextPage(true);
              }}
            />
          </Column>
        </Flexbox>
      </BlockContainer>
    </div>
  );
}
